/**
 * es-AR presentation formats (13-ui-ux-architecture.md §8).
 *
 * Date: dd/MM/yyyy · Date and time: dd/MM/yyyy HH:mm (24 h) · Money: ARS 1.234.567,89.
 *
 * Instants are shown in the tenant time zone (§8.1), never in the browser one. Every helper takes
 * an optional IANA zone and, when the caller names none, reads the active one: the zone of the
 * tenant the session is in (`activeTenant.timeZone`), which the session sets once with
 * {@link setActiveTimeZone} so that no call site has to carry it. Outside a tenant (the sign-in
 * screens, the platform console before it enters one) there is none, and the platform's own zone
 * stands in.
 */

export const LOCALE = 'es-AR';

/** Zone used while there is no tenant to take one from: the platform's own (`Tenant.DefaultTimeZone`). */
export const DEFAULT_TIME_ZONE = 'America/Argentina/Buenos_Aires';

let activeTimeZone = DEFAULT_TIME_ZONE;

/**
 * The zone the session named and this browser could not use, while there is one: the server
 * validates a zone with the platform's database and the browser formats with its own `Intl`, so a
 * zone one accepts the other may not know. The screens say so (`useUnusableTimeZone`) instead of
 * showing Buenos Aires hours without a word.
 */
let unusableTimeZone: string | null = null;

/**
 * Counts the times a live session brought another zone than the one already in force: what a
 * screen already painted does not know it was formatted with the old one (`useTimeZoneVersion`).
 */
let timeZoneVersion = 0;

const activeTimeZoneListeners = new Set<() => void>();

/** Whether this browser knows `zone` as an IANA zone: `Intl` throws a `RangeError` for one it does not. */
function isUsableTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Sets the zone every helper below reads when its caller names none: the one of the tenant the
 * session is in. Anything missing, or that this browser cannot use (a zone its database does not
 * know would make `Intl` throw on every date of every screen), falls back to
 * {@link DEFAULT_TIME_ZONE}, the same fallback a session with no tenant gets; a zone that was named
 * and could not be used is remembered ({@link getUnusableTimeZone}) so that it can be said.
 *
 * `repaint` says the zone comes from a session that is alive: if it differs from the one in force,
 * the screens already painted are told to be drawn again ({@link getTimeZoneVersion}). A session
 * that goes away (sign-out, a 401) only puts the default back and redraws nothing, because what is
 * on screen is about to be replaced. Whoever subscribed ({@link subscribeActiveTimeZone}) hears of
 * every change.
 */
export function setActiveTimeZone(
  zone: string | null | undefined,
  options: { repaint?: boolean } = {},
): void {
  const candidate = zone?.trim() ?? '';
  const usable = candidate !== '' && isUsableTimeZone(candidate);
  const next = usable ? candidate : DEFAULT_TIME_ZONE;
  const nextUnusable = candidate !== '' && !usable ? candidate : null;
  if (next === activeTimeZone && nextUnusable === unusableTimeZone) {
    return;
  }

  if (options.repaint === true && next !== activeTimeZone) {
    timeZoneVersion += 1;
  }

  activeTimeZone = next;
  unusableTimeZone = nextUnusable;
  activeTimeZoneListeners.forEach((listener) => listener());
}

/** The zone the helpers use when their caller names none (see {@link setActiveTimeZone}). */
export function getActiveTimeZone(): string {
  return activeTimeZone;
}

/** The zone the session named that this browser does not know, or null (see {@link setActiveTimeZone}). */
export function getUnusableTimeZone(): string | null {
  return unusableTimeZone;
}

/** How many times a live session moved the active zone (see {@link setActiveTimeZone}): a screen keyed by it is drawn again when it changes. */
export function getTimeZoneVersion(): number {
  return timeZoneVersion;
}

/**
 * Calls `listener` whenever the active zone, or the zone this browser could not use, changes.
 * Returns the function that stops it.
 */
export function subscribeActiveTimeZone(listener: () => void): () => void {
  activeTimeZoneListeners.add(listener);
  return () => {
    activeTimeZoneListeners.delete(listener);
  };
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

/** `dd/MM/yyyy HH:mm` in 24 h. */
export function formatDate(
  value: Date | string | number,
  timeZone: string = getActiveTimeZone(),
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  // Assembled by hand: browsers disagree on the separator (", " vs. " ") for the same locale.
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

/** `dd/MM/yyyy`, without the time part. */
export function formatDay(
  value: Date | string | number,
  timeZone: string = getActiveTimeZone(),
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * `yyyy-MM-dd` of the calendar day an instant falls on in `timeZone`: "today" for a date field, a
 * filter or a comparison against a date column. `toISOString().slice(0, 10)` is the day in UTC,
 * and from 21:00 in Buenos Aires (UTC-3) that is already tomorrow: a payment or a credit note
 * proposed with the next day's date. Every "today" of the console goes through here, so it reads
 * the same zone every date is shown in.
 */
export function todayIso(
  now: Date | string | number = new Date(),
  timeZone: string = getActiveTimeZone(),
): string {
  const date = toDate(now);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const parts = dayFormatter(timeZone).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  // Assembled by hand, like `formatDate`: no reliance on the locale's own order or separators.
  return `${get('year')}-${get('month')}-${get('day')}`;
}

const DAY_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone: `startOfDayInstant` asks for the day of an instant a dozen times. */
function dayFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = DAY_FORMATTERS.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat(LOCALE, {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    DAY_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * The day (`yyyy-MM-dd`) whose month a date field with no value opens its calendar on: the tenant's
 * today (`todayIso`), held to the range the field allows (`AM-314`). Mantine does the same with the
 * browser's clock, and the day the browser is in can be in another month than the tenant's. A
 * today before `minDate` gives `minDate` and one after `maxDate` gives `maxDate`: left outside the
 * range, the calendar would open on a month with every day disabled. Days of this shape compare as
 * strings, so no zone enters the comparison. Empty when the clock is not a valid date.
 */
export function calendarDefaultDay(minDate?: string, maxDate?: string): string {
  const today = todayIso();
  if (today === '') {
    return '';
  }
  if (minDate !== undefined && today < minDate) {
    return minDate;
  }
  if (maxDate !== undefined && today > maxDate) {
    return maxDate;
  }
  return today;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/u;
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/**
 * Whether `value` is a `yyyy-MM-dd` that names a day that exists: `2026-02-30` and `2026-13-40`
 * have the shape but not the day, and `Date` would silently move the first to 2 March and give an
 * `Invalid Date` for the second. A round trip through UTC is what tells them apart.
 */
export function isIsoDay(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) {
    return false;
  }

  const milliseconds = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(milliseconds) && new Date(milliseconds).toISOString().startsWith(value);
}

/**
 * The first instant of the calendar day `day` (`yyyy-MM-dd`) in `timeZone`: what a date a person
 * picked means as the lower bound of a filter by instant. Found by bisection over the instants whose
 * day in the zone is `day` or later, and not by subtracting an offset, so a zone that changes its
 * clock at midnight (Santiago goes from 24:00 to 01:00 on the first Sunday of September: that day
 * has no 00:00) answers the first instant that really belongs to the day. `Invalid Date` when `day`
 * is not a `yyyy-MM-dd` that names a day that exists (see {@link isIsoDay}).
 */
export function startOfDayInstant(day: string, timeZone: string = getActiveTimeZone()): Date {
  if (!isIsoDay(day)) {
    return new Date(Number.NaN);
  }

  const utcMidnight = Date.parse(`${day}T00:00:00Z`);

  // No zone is further than UTC-12 or UTC+14, so the local midnight of `day` lies between 14 h
  // before and 12 h after its UTC one: an hour of margin on each side gives an instant that is
  // still on the day before it (`before`) and one that is already on the day (`notBefore`).
  let before = Math.floor((utcMidnight - 15 * HOUR) / MINUTE);
  let notBefore = Math.floor((utcMidnight + 13 * HOUR) / MINUTE);
  while (notBefore - before > 1) {
    const middle = Math.floor((before + notBefore) / 2);
    if (todayIso(new Date(middle * MINUTE), timeZone) >= day) {
      notBefore = middle;
    } else {
      before = middle;
    }
  }

  return new Date(notBefore * MINUTE);
}

/** The last millisecond of the calendar day `day` (`yyyy-MM-dd`) in `timeZone`. */
export function endOfDayInstant(day: string, timeZone: string = getActiveTimeZone()): Date {
  if (!isIsoDay(day)) {
    return new Date(Number.NaN);
  }

  const next = startOfDayInstant(addDaysToIsoDay(day, 1), timeZone);
  return new Date(next.getTime() - 1);
}

/** The year of the calendar day `now` falls on in `timeZone`: what a list of years starts from. */
export function currentYear(
  now: Date | string | number = new Date(),
  timeZone: string = getActiveTimeZone(),
): number {
  return Number(todayIso(now, timeZone).slice(0, 4));
}

/**
 * A `yyyy-MM-dd` day moved by `days` (negative to go back). Plain calendar arithmetic, done in UTC
 * so that no zone or daylight-saving change can shift the result by a day.
 */
export function addDaysToIsoDay(day: string, days: number): string {
  const moved = new Date(`${day}T00:00:00Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}

/**
 * `ARS 1.234.567,89` - the code of the currency, a space and the amount with thousands in `.` and
 * decimals in `,` (§8, CONV-19). `AM-442`, rule 6 of the billing model: every amount carries the code
 * of its currency and never the symbol (`$` is shared by more than one currency), the same way the PDF
 * writes it (`PdfDocuments.FormatMoney`).
 */
export function formatMoney(amount: number, currency: string): string {
  return `${currency.trim().toUpperCase()} ${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

/**
 * A rate in its currency with up to the four decimals the column keeps (`numeric(14,4)`): two rates
 * that differ below the cent read alike with the two of `formatMoney` (`AM-296`).
 */
export function formatMoneyRate(amount: number, currency: string): string {
  return `${currency.trim().toUpperCase()} ${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount)}`;
}

/**
 * An alícuota de IVA from its fraction, always written "21 %" or "10,5 %" (`AM-442`; model, "IVA,
 * alícuota de IVA y su origen"): up to two decimals, none when it is a whole number.
 */
export function formatTaxRate(fraction: number): string {
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 }).format(
    Math.round(fraction * 10000) / 100,
  )} %`;
}

/** `AM-892`: a multiplier with the decimal comma of the model and up to four decimals ("1,5" for "×1,5"). */
export function formatFactor(value: number): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 4 }).format(value);
}

/** Whether the browser's own IANA database knows `zone`; an empty one is left to the platform's default. */
export function isKnownTimeZone(value: string): boolean {
  const zone = value.trim();
  if (zone.length === 0) {
    return true;
  }
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function firstOfMonthUtc(month: string): Date | null {
  const date = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1));
  return month.length === 7 && !Number.isNaN(date.getTime()) ? date : null;
}

/** "sep. 2026" for a `YYYY-MM` month (a label of a calendar month, never an instant), or the key when it is not one. */
export function formatMonthShort(month: string): string {
  const date = firstOfMonthUtc(month);
  return date === null
    ? month
    : new Intl.DateTimeFormat(LOCALE, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
        date,
      );
}

/** "octubre de 2026" for `2026-10`. */
export function formatMonthLong(month: string): string {
  const date = firstOfMonthUtc(month);
  return date === null
    ? month
    : new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        date,
      );
}

/** "octubre" for `2026-10`: the name of the month in words. */
export function formatMonthName(month: string): string {
  const date = firstOfMonthUtc(month);
  return date === null
    ? month
    : new Intl.DateTimeFormat(LOCALE, { month: 'long', timeZone: 'UTC' }).format(date);
}

/** Plain number with es-AR separators. */
export function formatNumber(value: number, fractionDigits = 2): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * A quantity of an invoice line or a billable entry: at least the two decimals of an hour, up to the
 * four of the column (`numeric(12,4)`). The amount is priced on the four, so showing only two prints
 * a line whose quantity times rate does not give its amount (0,78 h x 35,00 is not 27,42).
 */
export function formatQuantity(value: number): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

/**
 * A file size a person reads (`13` §4.3.2): `1,2 MB` rather than `1258291`. The units are the
 * binary ones the ceiling of `V-17` is stated in, and the separators are the es-AR ones of §8.
 * Bytes are shown whole - half a byte is not a thing - and everything above one kibibyte keeps one
 * decimal.
 */
export function formatBytes(value: number): string {
  const units = ['B', 'kB', 'MB', 'GB'];
  let size = Math.max(0, value);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  const rounded = new Intl.NumberFormat(LOCALE, {
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(size);
  return `${rounded} ${units[unit]!}`;
}

const RELATIVE = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

const RELATIVE_STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60_000],
  ['month', 30 * 24 * 60 * 60_000],
  ['day', 24 * 60 * 60_000],
  ['hour', 60 * 60_000],
  ['minute', 60_000],
];

/**
 * "hace 2 horas" (13 §4.2.3). The portal shows the last update this way because what the
 * requester asks is "is anybody looking at it", not on which calendar day it moved; the console
 * keeps the exact date of {@link formatDate}.
 */
export function formatRelative(value: Date | string | number, now: Date = new Date()): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const difference = date.getTime() - now.getTime();
  for (const [unit, milliseconds] of RELATIVE_STEPS) {
    const amount = Math.trunc(difference / milliseconds);
    if (amount !== 0) {
      return RELATIVE.format(amount, unit);
    }
  }

  return RELATIVE.format(0, 'minute');
}

/**
 * `DD/MM/YYYY` as typed into the start date, to the `YYYY-MM-DD` the API takes; `null` until the
 * whole date is there. Mantine's default parser reads the text through `dayjs` without the custom
 * format plugin, which takes `01/06/2026` for the 6th of January.
 */
export function parseDayInput(value: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (match === null) {
    return null;
  }
  const [, dayText = '', monthText = '', yearText = ''] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${yearText}-${monthText.padStart(2, '0')}-${dayText.padStart(2, '0')}`;
}

/**
 * A date as a person types it into a date field (`AM-321`): `DD/MM/YYYY`, the way the field shows it,
 * or the unambiguous `YYYY-MM-DD`; `null` while the text is not a whole, real date of either shape.
 */
export function parseTypedDay(value: string): string | null {
  const dayFirst = parseDayInput(value);
  if (dayFirst !== null) {
    return dayFirst;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (iso === null) {
    return null;
  }
  const [, year = '', month = '', day = ''] = iso;
  return parseDayInput(`${day}/${month}/${year}`);
}
